import React from "react";
import { FunnelChart } from "@/features/investors/components/charts/FunnelChart";
import { Heatmap } from "@/features/investors/components/charts/Heatmap";
import { StackedBars } from "@/features/investors/components/charts/StackedBars";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    InsightHtml,
    Micro,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const FUNNEL_COLORS = [CHART_COLORS.blue, CHART_COLORS.teal, CHART_COLORS.violet, CHART_COLORS.lime];

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const RetentionSection: React.FC<Props> = ({ data, insights }) => {
    const { retention, period } = data;
    const monthColumns = period.months.map((_, index) => `M${index}`);

    return (
        <section className="sec" id="sec-retention">
            <SectionHead
                index="04"
                title="Customer quality & retention"
                question={
                    <>
                        Question: <em>Do customers come back?</em>
                    </>
                }
            />
            <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 20 }}>
                <Micro
                    label="New accounts"
                    value={fmt.num(retention.newAccounts)}
                    sub="First completed operation this quarter"
                />
                <Micro
                    label="Existing accounts"
                    value={fmt.num(retention.existingAccounts)}
                    sub="Active with history before the quarter"
                />
                <Micro
                    label="Reactivated"
                    value={fmt.num(retention.reactivatedAccounts)}
                    sub="Returned after a dormant quarter"
                />
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1.15fr 1fr", alignItems: "start" }}>
                <div className="card">
                    <CardHeader
                        title="Cohort retention"
                        sub="% of new accounts still transacting, by first-operation month"
                    />
                    <Heatmap
                        rows={retention.cohortRetention.map((row) => ({
                            label: row.label,
                            sub: `${fmt.numShort(row.size)} accounts`,
                            cells: row.retentionPct.map((value, index) => ({
                                value,
                                isFuture: value === null && index > 0,
                            })),
                        }))}
                        columns={monthColumns}
                    />
                    <div className="chart-foot" style={{ border: 0, paddingTop: 8 }}>
                        <span className="ck" style={{ fontSize: 11.5, color: "var(--sub)" }}>
                            Darker = higher retention · within-quarter view (M0 = cohort month)
                        </span>
                    </div>
                </div>
                <div className="grid" style={{ gap: 20, alignContent: "start" }}>
                    <div className="card">
                        <CardHeader title="Repeat-usage funnel" sub="Active accounts by operation depth" />
                        <FunnelChart
                            steps={retention.repeatFunnel.map((step, index) => ({
                                label: step.label,
                                value: step.value,
                                color: FUNNEL_COLORS[index % FUNNEL_COLORS.length],
                            }))}
                            formatValue={fmt.num}
                        />
                    </div>
                    <div className="card">
                        <CardHeader title="Account growth composition" sub="Active accounts per month" />
                        <StackedBars
                            series={[
                                {
                                    label: "Existing",
                                    color: CHART_COLORS.blue,
                                    values: retention.monthlyComposition.map((month) => month.existingAccounts),
                                },
                                {
                                    label: "New",
                                    color: CHART_COLORS.teal,
                                    values: retention.monthlyComposition.map((month) => month.newAccounts),
                                },
                                {
                                    label: "Reactivated",
                                    color: CHART_COLORS.violet,
                                    values: retention.monthlyComposition.map(
                                        (month) => month.reactivatedAccounts
                                    ),
                                },
                            ]}
                            labels={retention.monthlyComposition.map((month) => month.label)}
                            height={170}
                            formatValue={fmt.num}
                        />
                        <div className="chart-foot">
                            {["Existing", "New", "Reactivated"].map((label, index) => (
                                <span key={label} className="ck">
                                    <span
                                        className="ck-dot"
                                        style={{
                                            background: [CHART_COLORS.blue, CHART_COLORS.teal, CHART_COLORS.violet][
                                                index
                                            ],
                                        }}
                                    />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.retention} />
            </div>
        </section>
    );
};
